import React from 'react';
import i18next from 'i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import Link from 'components/Link';
import Ad from 'components/Ad';
import ButtonHoverAnimation from 'components/ButtonHoverAnimation';
import Unibrow from 'components/Unibrow';
import WaffleBadge from 'components/packages/Waffle/WaffleBadge';
import { content as contentPropType, waffleMetadata } from 'lib/CustomPropTypes';
import {
  WaffleTeaseImage,
  WaffleHeadline,
  WaffleTextSummary,
  WaffleToggleButton,
} from 'components/packages/Waffle/Shared';
import CardOverlay from 'components/CardOverlay';

class WaffleCard extends React.Component {
  static propTypes = {
    card: contentPropType,
    cardsLength: PropTypes.number,
    vertical: PropTypes.string,
    inAdLayout: PropTypes.bool,
    adsOn: PropTypes.bool,
    metadata: waffleMetadata,
    index: PropTypes.number,
    useModal: PropTypes.bool,
  };

  static contextTypes = {
    stickyfill: PropTypes.objectOf(PropTypes.any),
  };

  static defaultProps = {
    card: {},
    cardsLength: 0,
    vertical: 'news',
    inAdLayout: true,
    adsOn: false,
    metadata: {},
    index: 0,
    useModal: false,
  };


  constructor(props) {
    super(props);
    this.state = { activateShowMore: false };
  }

  componentDidMount() {
    const { stickyfill } = this.context;
    if (this.isBoxflex()) {
      stickyfill.add(this.waffleCard);
    }
  }

  getType = () => {
    const { card: { type } } = this.props;
    return type;
  };

  isAd = () => {
    const type = this.getType();
    return type === 'boxflex' || type === 'boxinline' || type === 'midresponsive';
  };

  isBoxflex = () => this.getType() === 'boxflex';

  updateShowMore = (currentState) => {
    this.setState({ activateShowMore: currentState });
  }

  render() {
    const {
      card: {
        computedValues = {},
        id,
        type,
        metadata: itemMetadata = {},
        item,
      },
      cardsLength,
      vertical,
      inAdLayout,
      metadata: packageMetadata,
      index,
      useModal,
    } = this.props;

    const { nativeAd, source } = item || {};
    const { organization: { name: sponsorName } = {} } = source || {};
    const { showEyebrow = true } = packageMetadata || {};
    const { showTeaseImage: showTease = true } = itemMetadata || {};
    const { activateShowMore } = this.state;

    const cardIsLinkable = !!(computedValues.url);
    const cardIsNotLinkable = !cardIsLinkable;

    const eyebrowIsLinkable = !!(computedValues.unibrow
      && computedValues.unibrow.url && computedValues.unibrow.url.primary);

    const eyebrowShouldUsePrimaryColor = vertical !== 'news' || vertical !== 'globalcitizen' || vertical !== 'leftfield';
    const eyebrowShouldUserSecondaryColor = vertical === 'news' || vertical === 'globalcitizen' || vertical === 'leftfield';
    const eyebrowStyles = {
      'color-primary': eyebrowShouldUsePrimaryColor,
      'color-secondary': eyebrowShouldUserSecondaryColor,
    };

    let eyebrowColor;
    if (nativeAd) {
      eyebrowColor = 'white';
    } else if (eyebrowIsLinkable) {
      eyebrowColor = classNames(eyebrowStyles);
    } else {
      eyebrowColor = 'gray-80';
    }

    const cardColor = nativeAd ? 'bg-gray-100' : 'bg-white';
    const showDek = itemMetadata.showDek !== undefined
      ? itemMetadata.showDek
      : packageMetadata.showDek;
    const hasMoreInfo = itemMetadata && itemMetadata.moreInfoText && itemMetadata.moreInfoText !== '';

    return (
      <li
        data-contentid={id}
        ref={(waffleCard) => { this.waffleCard = waffleCard; }}
        key={id}
        className={classNames(
          'waffle-card',
          this.isBoxflex() ? 'df justify-center items-center sticky' : '',
          {
            'waffle-card__sticky-ad': this.isBoxflex(),
            'waffle-card__card-in-ad-layout': inAdLayout,
            paidContent: nativeAd,
          },
          cardColor,
          { 'waffle-card__disabled': cardIsNotLinkable },
        )}
      >
        {!this.isAd() ? (
          <div
            className="h-100 df flex-column"
            data-test="waffle-card--noAd"
          >
            <Link to={computedValues.url} className="db relative">
              <WaffleTeaseImage
                computedValues={computedValues}
                vertical={vertical}
                showTease={showTease}
              />
              <WaffleBadge
                index={index}
                cardsLength={cardsLength}
                metadata={{ ...packageMetadata, ...itemMetadata }}
                vertical={vertical}
                className="absolute bottom0 left0 z-1"
              />
            </Link>
            <section
              className={classNames(
                'ph5 ph7-xl df flex-column flex-grow-1',
                { 'pb8 pb9-xl': hasMoreInfo, 'pb5 pb7-xl': !hasMoreInfo, pt7: !showEyebrow },
              )}
            >
              <Unibrow
                unibrow={computedValues.unibrow}
                size="h5"
                className={classNames(
                  'founders-mono pb2 pt4 f2 ttu fw2 mv0 lh-none',
                  eyebrowColor,
                  { dn: !showEyebrow },
                )}
              />
              <WaffleHeadline
                url={computedValues.url}
                headline={computedValues.headline}
                nativeAd={nativeAd}
                sponsorName={sponsorName}
                vertical={vertical}
              />
              <WaffleTextSummary
                showDek={showDek}
                dekText={computedValues.dek}
                moreInfoText={itemMetadata.moreInfoText}
                mobileExpand
                showMoreInfo={activateShowMore}
                moreInfoClasses={classNames('dn-m', { db: activateShowMore, dn: !activateShowMore })}
              />
              {cardIsLinkable && activateShowMore && (
                <ButtonHoverAnimation
                  additionalClasses="dn-m"
                  url={computedValues.url}
                  type="link"
                  title={i18next.t('See story')}
                />
              )}
            </section>
            {hasMoreInfo
            && (
              <React.Fragment>
                <CardOverlay isActive={activateShowMore} useModal={useModal}>
                  <section
                    className={classNames(
                      'waffle-card__scrollable-more-info',
                      'ph5 ph7-xl pb2 pb9-xl',
                      { 'pt5 pt7-xl': !showEyebrow },
                    )}
                  >
                    <Unibrow
                      unibrow={computedValues.unibrow}
                      size="h5"
                      className={classNames(
                        'founders-mono pb2 pt4 f2 ttu fw2 mv0 lh-none',
                        eyebrowColor,
                        { dn: !showEyebrow },
                      )}
                    />
                    <WaffleHeadline
                      url={computedValues.url}
                      headline={computedValues.headline}
                      nativeAd={nativeAd}
                      sponsorName={sponsorName}
                      vertical={vertical}
                    />
                    <WaffleTextSummary
                      showDek={showDek}
                      showMoreInfo
                      dekText={computedValues.dek}
                      moreInfoText={itemMetadata.moreInfoText}
                    />
                    {cardIsLinkable && (
                      <ButtonHoverAnimation
                        url={computedValues.url}
                        type="link"
                        title={i18next.t('See story')}
                      />
                    )}
                  </section>
                  <WaffleToggleButton
                    className="w-100 ph5 fixed bottom0 left0 right0 z-1 dn-m"
                    activeText={i18next.t('Less')}
                    inactiveText={i18next.t('More')}
                    onChangeCallback={this.updateShowMore}
                    isActive={activateShowMore}
                  />
                </CardOverlay>
                <WaffleToggleButton
                  className="w-100 ph5 ph7-xl absolute bottom0 left0 right0 z-1"
                  activeText={i18next.t('Less')}
                  inactiveText={i18next.t('More')}
                  onChangeCallback={this.updateShowMore}
                  isActive={activateShowMore}
                />
              </React.Fragment>
            )}
          </div>
        ) : (
          <Ad
            {...packageMetadata}
            sticky={this.isBoxflex()}
            offsetViewport={type === 'boxinline' ? 50 : null}
            slot={type}
            adClass="waffle-card__ad"
            data-test="waffle-card--ad"
          />
        )}
      </li>
    );
  }
}

export default WaffleCard;
